import type { SlotTag } from './exercise-types';
import type { MuscleId } from './muscles';

export interface SessionSlot {
  slot: SlotTag;
  /** Overrides the exercise's own default when set. */
  sets?: number;
  repRange?: [number, number];
  /** Dropped first on a deload week or when you're short on time. */
  optional?: boolean;
  /** Keep the same exercise here for the whole block so progression means something. */
  anchor?: boolean;
  /**
   * Run this slot back-to-back with another one, resting only after the pair.
   *
   * Only ever pair movements that don't compete for the same muscle — a curl
   * and a pushdown, a leg extension and a leg curl. Done right it halves the
   * time an accessory block takes at no cost to the work done. Done wrong it
   * just makes both exercises worse. The named slot must exist in the same
   * session; a test enforces that.
   */
  supersetWith?: SlotTag;
  note?: string;
}

export interface SessionTemplate {
  id: string;
  name: string;
  /** One-line "what today is for". */
  focus: string;
  focusMuscles: MuscleId[];
  slots: SessionSlot[];
}

export interface Split {
  id: string;
  name: string;
  summary: string;
  /** Why you'd run this block, in plain language. */
  rationale: string;
  sessions: SessionTemplate[];
}

/**
 * Block A — the antagonist split you already run.
 *
 * Pairing a pushing muscle with the opposing arm muscle (chest with biceps,
 * back with triceps) means the arms come in fresh instead of being pre-fatigued
 * by the big lift, so you can actually load them. Forearms and traps get
 * slotted into the pull-ish days, abs finish every session.
 */
const ANTAGONIST: Split = {
  id: 'antagonist',
  name: 'Antagonist split',
  summary: 'Chest + biceps · Back + triceps · Legs + shoulders',
  rationale:
    'Your current split. Each day pairs a big muscle with an arm muscle that was not involved in the pressing or pulling, so your arms are fresh when you get to them. Abs close out every session; forearms and traps ride along on the arm days.',
  sessions: [
    {
      id: 'ant_chest_biceps',
      name: 'Chest + Biceps',
      focus: 'Heavy pressing, then curls with fresh arms',
      focusMuscles: ['chest', 'biceps', 'forearms', 'abs'],
      slots: [
        { slot: 'chest_primary', anchor: true, sets: 4, repRange: [5, 8] },
        { slot: 'chest_secondary', sets: 3, repRange: [8, 12] },
        { slot: 'chest_isolation', sets: 3, repRange: [12, 15], supersetWith: 'biceps_secondary' },
        { slot: 'biceps_primary', anchor: true, sets: 4, repRange: [8, 12] },
        { slot: 'biceps_secondary', sets: 3, repRange: [10, 14] },
        { slot: 'forearms', sets: 3, repRange: [12, 20], optional: true, supersetWith: 'abs_flexion' },
        { slot: 'abs_flexion', sets: 3 },
        { slot: 'abs_antiextension', sets: 2, optional: true },
      ],
    },
    {
      id: 'ant_back_triceps',
      name: 'Back + Triceps',
      focus: 'Vertical and horizontal pulling, then triceps',
      focusMuscles: ['lats', 'upper_back', 'traps', 'triceps', 'abs'],
      slots: [
        { slot: 'back_vertical', anchor: true, sets: 4, repRange: [6, 10] },
        { slot: 'back_horizontal', anchor: true, sets: 4, repRange: [8, 12] },
        { slot: 'back_isolation', sets: 3, repRange: [12, 15] },
        { slot: 'traps', sets: 3, repRange: [10, 15], supersetWith: 'triceps_secondary' },
        { slot: 'triceps_primary', sets: 4, repRange: [8, 12] },
        { slot: 'triceps_secondary', sets: 3, repRange: [12, 15] },
        { slot: 'abs_flexion', sets: 3, supersetWith: 'obliques' },
        { slot: 'obliques', sets: 2, optional: true },
      ],
    },
    {
      id: 'ant_legs_shoulders',
      name: 'Legs + Shoulders',
      focus: 'The big day — squat, hinge, then delts',
      focusMuscles: ['quads', 'hamstrings', 'glutes', 'calves', 'delts_side', 'abs'],
      slots: [
        { slot: 'quad_primary', anchor: true, sets: 4, repRange: [5, 8] },
        { slot: 'ham_primary', anchor: true, sets: 3, repRange: [8, 12] },
        { slot: 'quad_isolation', sets: 3, repRange: [12, 18], supersetWith: 'ham_isolation' },
        { slot: 'ham_isolation', sets: 3, repRange: [10, 15] },
        { slot: 'calves', sets: 4, repRange: [10, 15], supersetWith: 'abs_flexion' },
        { slot: 'delts_press', sets: 3, repRange: [8, 12] },
        { slot: 'delts_side', sets: 3, repRange: [12, 20], supersetWith: 'delts_rear' },
        { slot: 'delts_rear', sets: 3, repRange: [15, 20], optional: true },
        { slot: 'abs_flexion', sets: 3 },
      ],
    },
  ],
};

/**
 * Block B — true push / pull / legs.
 *
 * Everything that presses trains together and everything that pulls trains
 * together, so a muscle is never hit hard two days running by accident. Rear
 * delts and forearms get a proper home on pull day, which the antagonist split
 * tends to leave thin.
 */
const PPL: Split = {
  id: 'ppl',
  name: 'Push / Pull / Legs',
  summary: 'Push · Pull · Legs',
  rationale:
    'Groups every pressing muscle on one day and every pulling muscle on another, so nothing gets hit hard on back-to-back days by accident. Your triceps stop getting hammered the day after chest, and rear delts and forearms finally get a real slot instead of an afterthought.',
  sessions: [
    {
      id: 'ppl_push',
      name: 'Push',
      focus: 'Chest, delts and triceps together',
      focusMuscles: ['chest', 'delts_front', 'delts_side', 'triceps', 'abs'],
      slots: [
        { slot: 'chest_primary', anchor: true, sets: 4, repRange: [5, 8] },
        { slot: 'delts_press', anchor: true, sets: 3, repRange: [6, 10] },
        { slot: 'chest_secondary', sets: 3, repRange: [8, 12] },
        { slot: 'chest_isolation', sets: 3, repRange: [12, 15] },
        { slot: 'delts_side', sets: 4, repRange: [12, 20], supersetWith: 'triceps_secondary' },
        { slot: 'triceps_primary', sets: 3, repRange: [8, 12] },
        { slot: 'triceps_secondary', sets: 3, repRange: [12, 15] },
        { slot: 'abs_antiextension', sets: 3 },
      ],
    },
    {
      id: 'ppl_pull',
      name: 'Pull',
      focus: 'Back, rear delts, biceps and grip',
      focusMuscles: ['lats', 'upper_back', 'delts_rear', 'traps', 'biceps', 'forearms', 'abs'],
      slots: [
        { slot: 'back_vertical', anchor: true, sets: 4, repRange: [6, 10] },
        { slot: 'back_horizontal', anchor: true, sets: 4, repRange: [8, 12] },
        { slot: 'back_isolation', sets: 3, repRange: [12, 15] },
        { slot: 'delts_rear', sets: 3, repRange: [15, 20], supersetWith: 'traps' },
        { slot: 'traps', sets: 3, repRange: [10, 15] },
        { slot: 'biceps_primary', anchor: true, sets: 3, repRange: [8, 12] },
        { slot: 'biceps_secondary', sets: 3, repRange: [10, 14], supersetWith: 'forearms' },
        { slot: 'forearms', sets: 3, repRange: [12, 20], optional: true },
        { slot: 'abs_flexion', sets: 3 },
      ],
    },
    {
      id: 'ppl_legs',
      name: 'Legs',
      focus: 'Squat, hinge, and everything below the waist',
      focusMuscles: ['quads', 'hamstrings', 'glutes', 'calves', 'adductors', 'abs'],
      slots: [
        { slot: 'quad_primary', anchor: true, sets: 4, repRange: [5, 8] },
        { slot: 'ham_primary', anchor: true, sets: 4, repRange: [8, 12] },
        { slot: 'quad_secondary', sets: 3, repRange: [8, 12] },
        { slot: 'ham_isolation', sets: 3, repRange: [10, 15] },
        { slot: 'glutes', sets: 3, repRange: [10, 15] },
        { slot: 'calves', sets: 4, repRange: [10, 15], supersetWith: 'abs_flexion' },
        { slot: 'adductors', sets: 2, repRange: [15, 20], optional: true, supersetWith: 'obliques' },
        { slot: 'abs_flexion', sets: 3 },
        { slot: 'obliques', sets: 2, optional: true },
      ],
    },
  ],
};

export const SPLITS: Split[] = [ANTAGONIST, PPL];

export const SPLIT_BY_ID: Record<string, Split> = Object.fromEntries(
  SPLITS.map((s) => [s.id, s]),
);

export function getSplit(id: string): Split {
  return SPLIT_BY_ID[id] ?? ANTAGONIST;
}

export function getSessionTemplate(splitId: string, sessionId: string): SessionTemplate | undefined {
  return getSplit(splitId).sessions.find((s) => s.id === sessionId);
}
